import { Button, Chip, Stack, styled, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { DynamicIcon, EllipsesMenu } from 'src/components';
import { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { getSendUnlessLabel } from './hooks/use-send-unless-conditions';

type BannerVariant = 'default' | 'editing';

type BannerStatus = 'scheduled' | 'discarded';

interface ScheduledBannerProps {
  scheduledLabel: string;
  onView: () => void;
  onReschedule: () => void;
  onDiscard: () => void;
  isSaving?: boolean;
  conditions?: DraftScheduleSendUnless[] | null;
  isEditing?: boolean;
  onDoneEditing?: () => void;
  status?: BannerStatus;
  discardedLabel?: string;
  attachmentCount?: number;
}

const BannerContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'status',
})<{ variant: BannerVariant; status: BannerStatus }>(({ theme, variant, status }) => {
  const palette = theme.palette.custom?.OV?.sys;

  const colorsByState = {
    default: {
      bg: palette?.primaryContainer ?? theme.palette.info.light,
      fg: palette?.onPrimaryContainer ?? theme.palette.info.contrastText,
    },
    editing: {
      bg: palette?.secondaryContainer ?? theme.palette.warning.light,
      fg: palette?.onSecondaryContainer ?? theme.palette.warning.contrastText,
    },
    discarded: {
      bg:
        palette?.surfaceVariant ??
        (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]),
      fg: palette?.onSurfaceVariant ?? theme.palette.text.primary,
    },
  } as const;

  let chosen = colorsByState.default;

  if (status === 'discarded') {
    chosen = colorsByState.discarded;
  } else if (variant === 'editing') {
    chosen = colorsByState.editing;
  }

  return {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    backgroundColor: chosen.bg,
    color: chosen.fg,
  };
});

const ActionsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ConditionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

export const ScheduledBanner = ({
  scheduledLabel,
  onView,
  onReschedule,
  onDiscard,
  isSaving = false,
  conditions,
  isEditing = false,
  onDoneEditing,
  status = 'scheduled',
  discardedLabel,
  attachmentCount = 0,
}: ScheduledBannerProps): React.JSX.Element => {
  const variant: BannerVariant = isEditing ? 'editing' : 'default';

  let leadingIcon = '';
  if (isEditing) {
    leadingIcon = 'edit_note';
  } else if (status === 'discarded') {
    leadingIcon = 'do_not_disturb_on';
  }

  let primaryLabel = '';
  if (isEditing) {
    if (status === 'discarded') {
      primaryLabel = 'Editing unsent message';
    } else {
      primaryLabel = 'Editing scheduled message';
    }
  } else if (status === 'discarded') {
    primaryLabel = 'Message not sent';
  }

  const uniqueConditionLabels = useMemo(() => {
    if (!conditions?.length) return [];
    const seen = new Set<string>();
    return conditions
      .map((c) => getSendUnlessLabel(c.type))
      .filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  }, [conditions]);

  const showScheduledLine = Boolean(scheduledLabel && (isEditing || status === 'scheduled'));
  const showHeaderRow = Boolean(primaryLabel || isSaving || leadingIcon);
  const showDiscardedMeta = status === 'discarded' && !isEditing;
  const hasConditions = uniqueConditionLabels.length > 0;

  return (
    <BannerContainer direction="row" variant={variant} status={status}>
      {/* spacing is conditional to avoid margin when no title/header */}
      <Stack spacing={showHeaderRow ? 0.75 : 0} alignItems="flex-start">
        {showHeaderRow ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {!!leadingIcon && <DynamicIcon name={leadingIcon} weight={400} />}
            {!!primaryLabel && (
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'pre-wrap' }}>
                {primaryLabel}
              </Typography>
            )}
            {isSaving ? (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Saving…
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {showScheduledLine ? (
          <Stack direction="row" spacing={0} margin={0} gap="5px" alignItems="center">
            <DynamicIcon name="schedule" weight={400} sx={{ opacity: 0.8 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', opacity: 0.9 }}>
              Scheduled for {scheduledLabel}
            </Typography>
            {attachmentCount > 0 ? (
              <Stack
                direction="row"
                gap="2px"
                alignItems="center"
                sx={{ opacity: 0.9 }}
                title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
              >
                <DynamicIcon name="attach_file" weight={400} sx={{ fontSize: 16 }} />
                <Typography variant="body2">{attachmentCount}</Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {showDiscardedMeta ? (
          <Stack spacing={0.5}>
            {scheduledLabel ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.85 }}>
                <DynamicIcon name="schedule" weight={400} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  Was scheduled for {scheduledLabel}
                </Typography>
              </Stack>
            ) : null}
            {discardedLabel ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.85 }}>
                <DynamicIcon name="history_toggle_off" weight={400} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  Cancelled on {discardedLabel}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {hasConditions ? (
          <ConditionsRow>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {status === 'discarded' ? 'Canceled due to conditions:' : 'Cancel send if:'}
            </Typography>
            {uniqueConditionLabels.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{ backgroundColor: 'rgba(0,0,0,0.08)', color: 'inherit' }}
              />
            ))}
          </ConditionsRow>
        ) : null}
      </Stack>

      <ActionsContainer>
        {isEditing ? (
          <Button
            size="small"
            variant="contained"
            color="inherit"
            onClick={onDoneEditing ?? onView}
            sx={{
              color: 'inherit',
              backgroundColor: 'rgba(255,255,255,0.18)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)' },
            }}
          >
            Done
          </Button>
        ) : (
          <Button size="small" variant="outlined" color="inherit" onClick={onView}>
            View message
          </Button>
        )}
        <EllipsesMenu
          options={[
            { label: 'Reschedule Message', onClick: onReschedule, icon: 'schedule' },
            {
              label: status === 'discarded' ? 'Remove message' : 'Cancel schedule',
              onClick: onDiscard,
              icon: 'delete',
            },
          ]}
        />
      </ActionsContainer>
    </BannerContainer>
  );
};

export default ScheduledBanner;
